/**
 * A prompt's basic score, computed in the page without any AI — free, instant, and the same in
 * the browser and the desktop app. It checks what makes a prompt easy for an agent to do well:
 * a clear goal, context, acceptance criteria, a bounded scope, and no vague words standing in for
 * a decision. The AI review (Pro) goes further and rewrites it.
 */

export interface PromptCheck {
  key: 'goal' | 'context' | 'criteria' | 'scope' | 'vague' | 'length'
  ok: boolean
}

export interface PromptScore {
  score: number
  checks: PromptCheck[]
}

const GOAL = /\b(add|create|fix|implement|write|remove|refactor|update|build|make|test|migrate|rename|adicion|cri[ae]|corrij|corrig|implement|escrev|remov|refator|atualiz|constru|faç|faz|test|migr|renome)/i
const CONTEXT = /(`[^`]+`|\b[\w./-]+\.(ts|tsx|js|cs|py|go|rs|java|sql|md|json|yml|yaml)\b|\/[\w-]+\/|\b(endpoint|component|componente|função|function|class|classe|tabela|table|page|página|tela|screen|api|module|módulo)\b)/i
const CRITERIA = /\b(should|must|when|so that|expect|returns?|done when|acceptance|test|deve|quando|para que|retorn|esperad|critério|pronto quando|teste)/i
const SCOPE = /\b(only|just|do not|don't|without|except|apenas|só|somente|não|sem|exceto)\b/i
const VAGUE = /\b(better|improve it|nicer|somehow|etc\.?|stuff|things|melhor(ar)?|arrum(a|ar) isso|dá um jeito|coisas|tipo assim)\b/i

export function scorePrompt(prompt: string): PromptScore {
  const text = prompt.trim()
  const words = text.split(/\s+/).filter(Boolean).length
  const checks: PromptCheck[] = [
    { key: 'goal', ok: GOAL.test(text) },
    { key: 'context', ok: CONTEXT.test(text) },
    { key: 'criteria', ok: CRITERIA.test(text) },
    { key: 'scope', ok: SCOPE.test(text) },
    { key: 'vague', ok: !VAGUE.test(text) },
    { key: 'length', ok: words >= 8 && words <= 600 },
  ]
  const weights: Record<PromptCheck['key'], number> = { goal: 25, context: 20, criteria: 25, scope: 10, vague: 10, length: 10 }
  const score = text ? checks.reduce((sum, c) => sum + (c.ok ? weights[c.key] : 0), 0) : 0
  return { score, checks }
}

/** The AI review's answer: the JSON object tool.prompt-review asks for, found in the agent's reply. */
export interface PromptReview {
  score: number
  issues: string[]
  improved: string
}

export function readReview(reply: string | null | undefined): PromptReview | null {
  if (!reply) return null
  const start = reply.indexOf('{')
  const end = reply.lastIndexOf('}')
  if (start < 0 || end <= start) return null
  try {
    const value = JSON.parse(reply.slice(start, end + 1)) as Partial<PromptReview>
    if (typeof value.improved !== 'string') return null
    return {
      score: typeof value.score === 'number' ? Math.max(0, Math.min(100, Math.round(value.score))) : 0,
      issues: Array.isArray(value.issues) ? value.issues.filter((i): i is string => typeof i === 'string') : [],
      improved: value.improved,
    }
  } catch {
    return null
  }
}
